// =====================================================================
// Faz K (2026-05-06) — Cross-sell Intelligence
// =====================================================================
// "Bu ürünü alanların aynı sepette aldığı diğer ürünler" raporu.
// Engine: services/cross-sell.js (DB self-join, gece cron'la güncellenir)
//
// Endpoint:
//   GET  /api/admin/insights/cross-sell?limit=50  -> pairs + stats
//   POST /api/admin/insights/cross-sell/recompute -> manuel yeniden hesap
//
// Owner kullanım senaryoları:
//   - Yeni "X+Y bundle" reklam fikri (en yüksek confidence pair'lar)
//   - Sepet sayfası "Bunları da aldılar" widget'ının ham verisi
//   - Stok planlama: A çok satıyorsa B de stokta tutulmalı
// =====================================================================
import React, { useEffect, useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Button, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ConnectingAirportsIcon from "@mui/icons-material/ConnectingAirports";
import API from "../services/api";
import toast from "react-hot-toast";

const fmtMoney = (v) => v == null ? "—" :
  Number(v).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("tr-TR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
}) : "henüz hesap yok";

// Confidence (0-1) → renkli chip etiketi
// >= 0.5 → çok güçlü ("aldıkların yarısından fazlası bunu da aldı")
// >= 0.3 → güçlü
// >= 0.15 → orta
// < 0.15 → zayıf (eşik MIN_CONFIDENCE = 0.10)
const confidenceMeta = (c) => {
  const pct = Math.round(c * 100);
  if (c >= 0.5)  return { label: `%${pct} ÇOK GÜÇLÜ`,  color: "#10b981" };
  if (c >= 0.3)  return { label: `%${pct} GÜÇLÜ`,      color: "#22c55e" };
  if (c >= 0.15) return { label: `%${pct} ORTA`,       color: "#f59e0b" };
  return                { label: `%${pct} ZAYIF`,      color: "#94a3b8" };
};

export default function CrossSell() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [recomputing, setRecomputing] = useState(false);

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/cross-sell?limit=100")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const recompute = async () => {
    if (!window.confirm("Tüm cross-sell pair'larını yeniden hesapla? (mevcut sipariş verisinden, ~1-5 saniye)")) return;
    setRecomputing(true);
    try {
      const r = await API.post("/api/admin/insights/cross-sell/recompute");
      toast.success(`✓ ${r.data.pairs} pair hesaplandı (${r.data.duration_ms}ms)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setRecomputing(false);
    }
  };

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const stats = data.stats || {};
  const pairs = data.pairs || [];

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Cross-Sell Intelligence</Typography>
          <Typography variant="body2" color="text.secondary">
            "Bunu alanların birlikte aldığı diğer ürünler" — bundle/promo fikri için
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Yenile">
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained" startIcon={<AutorenewIcon />}
            onClick={recompute} disabled={recomputing}
          >
            {recomputing ? "Hesaplanıyor..." : "Yeniden Hesapla"}
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Toplam Pair</Typography>
            <Typography variant="h4" fontWeight={800}>{stats.total_pairs || 0}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Ürün Kapsama</Typography>
            <Typography variant="h4" fontWeight={800} color="#1565C0">{stats.products_covered || 0}</Typography>
            <Typography variant="caption" color="text.disabled">en az 1 pair'ı olan ürün</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="caption" color="text.secondary">Son Hesaplama</Typography>
            <Typography variant="h6" fontWeight={700}>{fmtDateTime(stats.last_calculated)}</Typography>
            <Typography variant="caption" color="text.disabled">
              Algoritma: order_items self-join, min 2 birlikte sipariş + min %10 confidence
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Veri yoksa rehber */}
      {pairs.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Henüz cross-sell veri yok. <strong>"Yeniden Hesapla"</strong> butonuna basın —
          mevcut sipariş geçmişinden çiftler hesaplanacak. Yeterli sipariş yoksa pair çıkmayabilir
          (eşik: en az 2 farklı siparişte birlikte alınmış olmak).
        </Alert>
      )}

      {/* Pair tablosu */}
      {pairs.length > 0 && (
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="h6" fontWeight={700}>
              Top {pairs.length} Pair
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (confidence azalan sıra)
              </Typography>
            </Typography>
          </Box>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell><strong>Anchor Ürün</strong></TableCell>
                <TableCell width={50} align="center"></TableCell>
                <TableCell><strong>Birlikte Alınan</strong></TableCell>
                <TableCell align="center"><strong>Birlikte</strong></TableCell>
                <TableCell><strong>Confidence</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pairs.map((p, i) => {
                const cm = confidenceMeta(Number(p.confidence) || 0);
                return (
                  <TableRow key={`${p.product_id}-${p.paired_id}-${i}`} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={p.product_image} variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: "#f1f5f9" }}
                        >📦</Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {p.product_name}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            #{p.product_id} · {fmtMoney(p.product_price)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <ConnectingAirportsIcon sx={{ color: "#64748b", fontSize: 20 }} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          src={p.paired_image} variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: "#f1f5f9" }}
                        >🛒</Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={600} sx={{ lineHeight: 1.2 }}>
                            {p.paired_name}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">
                            #{p.paired_id} · {fmtMoney(p.paired_price)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight={800}>{p.co_count}</Typography>
                      <Typography variant="caption" color="text.disabled">sipariş</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cm.label}
                        size="small"
                        sx={{ bgcolor: cm.color, color: "white", fontWeight: 700, fontSize: 11 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </Box>
  );
}
